import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { Sparkles, Mountain, Check, X, Inbox, Send } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const TABS = [
    { id: "received", label: "Received", icon: Inbox },
    { id: "sent", label: "Sent", icon: Send },
];

export default function Invites() {
    const [tab, setTab] = useState("received");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async (t) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/invites`, { params: { direction: t } });
            setItems(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(tab); }, [tab]);

    const respond = async (inv, action) => {
        try {
            await api.post(`/invites/${inv.id}/respond`, { action });
            toast.success(`Invite ${action === "accept" ? "accepted" : "declined"}`);
            load(tab);
            if (action === "accept") {
                // Open a thread with the sender
                const { data } = await api.post("/messages/threads", { recipient_user_id: inv.sender_id });
                navigate(`/messages/${data.id}`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="invites-page">
            <TopBar title="Invites" />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="mb-6">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-2">Adventure invites</div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl text-midnight-pine tracking-tighter">Your invitations</h1>
                </div>
                <div className="flex border-b border-ridge-line mb-5" data-testid="invites-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                data-testid={`invite-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-all ${
                                    active ? "border-ember-orange text-ember-orange" : "border-transparent text-slate-gray hover:text-granite"
                                }`}
                            >
                                <Icon className="w-4 h-4" /> {t.label}
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-24 bg-snowcap border border-ridge-line rounded-lg animate-pulse" />)}</div>
                ) : items.length === 0 ? (
                    <div className="bg-snowcap border border-ridge-line rounded-lg p-10 text-center" data-testid="invites-empty">
                        <Sparkles className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                        <p className="text-sm text-slate-gray">
                            {tab === "received" ? "No invites yet — share what you love and people will reach out." : "You haven't sent any invites yet."}
                        </p>
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {items.map((inv) => (
                            <li
                                key={inv.id}
                                data-testid={`invite-${inv.id}`}
                                className="bg-snowcap border border-ridge-line rounded-lg p-4 flex flex-col sm:flex-row gap-4"
                            >
                                {inv.adventure?.cover_photo || inv.adventure?.photos?.[0] ? (
                                    <Link to={`/adventures/${inv.adventure_id}`} className="w-full sm:w-32 h-24 rounded-md overflow-hidden bg-trail-tan shrink-0">
                                        <img src={inv.adventure.cover_photo || inv.adventure.photos[0]} alt="" className="w-full h-full object-cover" />
                                    </Link>
                                ) : (
                                    <div className="w-full sm:w-32 h-24 rounded-md bg-trail-tan flex items-center justify-center"><Mountain className="w-6 h-6 text-midnight-pine" /></div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-slate-gray mb-1">
                                        {tab === "received" ? `From ${inv.sender?.name || "Someone"}` : `To ${inv.recipient?.name || "Someone"}`}
                                        {" · "}
                                        {formatDistanceToNow(new Date(inv.created_at), { addSuffix: true })}
                                    </div>
                                    <Link to={`/adventures/${inv.adventure_id}`} className="font-display font-black text-base text-midnight-pine hover:text-ember-orange transition-colors line-clamp-1">
                                        {inv.adventure?.title || "Adventure"}
                                    </Link>
                                    {inv.message && (
                                        <p className="text-sm text-granite mt-1 line-clamp-2">{inv.message}</p>
                                    )}
                                    <div className="flex items-center gap-2 mt-3">
                                        <span
                                            data-testid={`invite-status-${inv.id}`}
                                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest capitalize ${
                                                inv.status === "pending" ? "bg-trail-tan text-midnight-pine" :
                                                inv.status === "accepted" ? "bg-moss/15 text-moss" : "bg-trail-red/15 text-trail-red"
                                            }`}
                                        >{inv.status}</span>
                                        {tab === "received" && inv.status === "pending" && (
                                            <>
                                                <button
                                                    data-testid={`invite-accept-${inv.id}`}
                                                    onClick={() => respond(inv, "accept")}
                                                    className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-ember-orange text-white text-xs font-bold hover:bg-ember-dark transition-all"
                                                >
                                                    <Check className="w-3.5 h-3.5" /> Accept
                                                </button>
                                                <button
                                                    data-testid={`invite-decline-${inv.id}`}
                                                    onClick={() => respond(inv, "decline")}
                                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-ridge-line text-granite text-xs font-bold hover:bg-alpine-mist transition-all"
                                                >
                                                    <X className="w-3.5 h-3.5" /> Decline
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
